import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { QuestionRuleFormFields, RuleAddProps, SaveRulesValues } from '@app/src/types/questionRules'
import { Question, QuestionRuleTypes } from '@app/src/types/resourceExplorer'
import { QuestionTypes } from '@app/src/wf-constants'
import { Stack } from '@mui/material'
import React, { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import Select, { Option } from '../../Form/Select/ControlledSelect'
import { useDrawer } from '../DrawerContext'
import DrawerView, { DrawerViewProps } from '../DrawerView'

export enum QuestionRulesConditions {
  FileUploaded = 'fileUploaded',
  Skipped = 'skipped',
}

const DrawerViewAddQuestionRule: React.FC<RuleAddProps & Omit<DrawerViewProps, 'title'>> = ({
  questionToShow,
  questionsInTemplate,
  sectionsInTemplate,
  handleSaveRule,
  rules,
  defaultValues,
  removeRule,
  currentRuleIndex = -1,
  questionInTemplate,
  ...props
}) => {
  const { formatMessage } = useIntl()
  const { closeDrawer } = useDrawer()
  const { control, handleSubmit, register, errors } = useForm()

  const otherQuestionsInTemplate = useMemo(
    () =>
      questionsInTemplate.filter(question => Number(question.questionId ?? question.id) !== Number(questionToShow?.id)),
    [questionsInTemplate, questionToShow?.id],
  )

  const fetchPayload = useMemo(
    () => ({
      filter: [
        {
          name: 'id',
          filters: [
            {
              value: otherQuestionsInTemplate.map(question => question.questionId ?? question.id),
              operator: Operators.In,
            },
          ],
        },
      ],
      include: [],
    }),
    [otherQuestionsInTemplate],
  )

  const { items: questions, isLoading: isLoadingQuestions } = useFetchCollectionWithPost<Question>({
    key: FetchKey.Questions,
    endpoint: endpoints.questionCollection,
    payload: fetchPayload,
    options: { enabled: Boolean(otherQuestionsInTemplate.length) },
  })

  const handleSave = useCallback(
    (values: SaveRulesValues) => {
      handleSaveRule(values)
      closeDrawer()
    },
    [handleSaveRule, closeDrawer],
  )

  const getQuestionOptions = useCallback(() => {
    const allRules = sectionsInTemplate.flatMap(section =>
      section.questions.flatMap(question => question.questionnaireQuestionRules ?? []),
    )

    const questionsWithRulesTargetingCurrent = allRules
      .filter(rule => rule.sectionQuestionTargets.some(target => target.targetQuestionId === questionToShow.id))
      .map(rule => Number(rule.questionId))

    const mappedQuestions = questions
      .filter(question => {
        if (question.id === questionToShow.id) return false
        if (questionsWithRulesTargetingCurrent.includes(Number(question.id))) return false
        return true
      })
      .map(question => {
        const questionSectionIndex = sectionsInTemplate.findIndex(section =>
          section.questions.some(q => Number(q.questionId) === Number(question.id)),
        )
        const questionSection = sectionsInTemplate[questionSectionIndex]
        const questionIndex =
          questionSection?.questions.findIndex(q => Number(q.questionId) === Number(question.id)) ?? 0
        return {
          question,
          questionSectionIndex,
          questionIndex,
          sectionName: `${questionSectionIndex + 1}. ${questionSection?.title || formatMessage({ id: 'templateBuilder.questionRules.untitledSection' })}`,
        }
      })

    mappedQuestions.sort((a, b) => {
      if (a.questionSectionIndex !== b.questionSectionIndex) {
        return a.questionSectionIndex - b.questionSectionIndex
      }
      return a.questionIndex - b.questionIndex
    })

    return mappedQuestions.map(({ question, questionSectionIndex, questionIndex, sectionName }) => ({
      label: `${questionSectionIndex + 1}.${questionIndex + 1}. ${question.questionText}`,
      value: { targetQuestionId: question.id },
      additionalText: sectionName,
    }))
  }, [sectionsInTemplate, questions, questionToShow.id, formatMessage])

  const getConditionOptions = useCallback(() => {
    const options =
      questionToShow.questionType.name === QuestionTypes.File
        ? [
            {
              label: formatMessage({ id: 'templateBuilder.questionRules.fileUploaded' }),
              value: QuestionRulesConditions.FileUploaded,
              disabled: rules?.some(rule => rule.conditionValue === QuestionRulesConditions.FileUploaded),
            },
          ]
        : questionToShow.requestCollection?.items.map(option => ({
            label: option.displayText,
            value: option.value,
            disabled: rules?.some(rule => rule.conditionValue === option.value),
          })) ?? []

    if (!questionInTemplate.isRequired) {
      options.push({
        label: formatMessage({ id: 'statistics.dataInsights.dontHaveThis' }),
        value: QuestionRulesConditions.Skipped,
        disabled: rules?.some(rule => rule.conditionValue === QuestionRulesConditions.Skipped),
      })
    }

    return options
  }, [questionToShow, questionInTemplate.isRequired, rules, formatMessage])

  const questionOptions = useMemo(() => getQuestionOptions(), [getQuestionOptions])
  const conditionOptions = useMemo(() => getConditionOptions(), [getConditionOptions])

  const findSelectedTargetValue = useCallback(
    (value: { targetQuestionId: number }, option: Option<{ targetQuestionId: number }>) =>
      Number(value.targetQuestionId) === Number(option.value.targetQuestionId),
    [],
  )

  return (
    <DrawerView
      title={formatMessage({ id: 'templateBuilder.questionRules.addRules' })}
      onFormSubmit={handleSubmit(handleSave)}
      buttons={[
        {
          label: formatMessage({ id: 'general.cancel' }),
          onClick: closeDrawer,
        },
        ...(removeRule
          ? [
              {
                label: formatMessage({ id: 'general.remove' }),
                sx: { color: 'semantic.error' },
                onClick: () => {
                  removeRule(currentRuleIndex)
                  closeDrawer()
                },
              },
            ]
          : []),
        {
          label: formatMessage({ id: 'general.save' }),
          variant: 'contained',
          type: 'submit',
        },
      ]}
      stackButtons
      {...props}
    >
      <Stack p={2} spacing={3}>
        <input
          type="hidden"
          name={QuestionRuleFormFields.QuestionId}
          defaultValue={questionToShow.id}
          ref={register()}
        />
        <Select
          options={[
            {
              label: formatMessage({ id: 'templateBuilder.questionRules.skip' }),
              value: QuestionRuleTypes.Skip,
            },
          ]}
          fieldLabel={formatMessage({ id: 'templateBuilder.questionRules.ruleType' })}
          enableAutoSelect
          name={QuestionRuleFormFields.RuleType}
          defaultValue={QuestionRuleTypes.Skip}
          control={control}
          rules={{ required: formatMessage({ id: 'form.validation.required' }) }}
          error={errors?.ruleType}
        />
        <Select
          options={conditionOptions}
          fieldLabel={formatMessage({ id: 'templateBuilder.questionRules.ifAnswerIs' })}
          name={QuestionRuleFormFields.ConditionValue}
          defaultValue={defaultValues?.conditionValue}
          control={control}
          error={errors?.conditionValue}
          rules={{ required: formatMessage({ id: 'form.validation.required' }) }}
        />
        <Select
          multiple
          options={questionOptions}
          fieldLabel={formatMessage({ id: 'templateBuilder.questionRules.skipFollowingQuestions' })}
          name={QuestionRuleFormFields.SectionQuestionTargets}
          defaultValue={defaultValues?.sectionQuestionTargets}
          control={control}
          limitTags={10}
          isLoading={isLoadingQuestions}
          findSelectedValue={findSelectedTargetValue}
          error={errors?.sectionQuestionTargets}
          rules={{ required: formatMessage({ id: 'form.validation.required' }) }}
          helperText={formatMessage({ id: 'templateBuilder.questionRules.skipFollowingQuestionsHelperText' })}
          groupBy={option => option.additionalText || ''}
        />
      </Stack>
    </DrawerView>
  )
}

export default DrawerViewAddQuestionRule
